import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { can } from "@/lib/permissions";
import { MessageCircle, Plus, Trash2, Send, Loader2, Clock, X } from "lucide-react";

/** Comma/Enter chip input for phone numbers. */
function PhoneChips({ phones, onChange }) {
  const [text, setText] = useState("");
  const add = (raw) => {
    const parts = String(raw).split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...phones];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setText("");
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      {phones.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {phones.map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-sm">
              {p}
              <button type="button" onClick={() => onChange(phones.filter((x) => x !== p))} className="text-emerald-400 hover:text-emerald-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); } }}
        onBlur={() => text && add(text)}
        placeholder="Phone number (e.g. 9324989606) — Enter to add"
        className="border-0 shadow-none focus-visible:ring-0 px-0 h-8"
      />
    </div>
  );
}

const WhatsAppNotificationSetup = ({ user, onLogout }) => {
  const canManage = can(user, "notifications:manage");
  const [actions, setActions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");     // action_type currently sending/saving
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ action_type: "", is_enabled: true, recipient_phones: [] });
  const [editing, setEditing] = useState(null);   // action_type being edited
  const [editPhones, setEditPhones] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, cRes] = await Promise.all([
        axios.get(`${API}/whatsapp-notifications/admin/actions`),
        axios.get(`${API}/whatsapp-notifications/admin`),
      ]);
      setActions(aRes.data.actions || []);
      setConfigs(cRes.data.configs || []);
    } catch (e) {
      toast.error("Failed to load WhatsApp notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const actionMeta = (value) => actions.find((a) => a.value === value) || { label: value, description: "", schedule: "" };
  const configured = new Set(configs.map((c) => c.action_type));
  const available = actions.filter((a) => !configured.has(a.value));

  const openAdd = () => {
    setForm({ action_type: available[0]?.value || "", is_enabled: true, recipient_phones: [] });
    setShowAdd(true);
  };

  const create = async () => {
    if (!form.action_type) { toast.error("Select a notification type"); return; }
    if (!form.recipient_phones.length) { toast.error("Add at least one phone number"); return; }
    setBusy("create");
    try {
      await axios.post(`${API}/whatsapp-notifications/admin`, form);
      toast.success("WhatsApp notification created");
      setShowAdd(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setBusy(""); }
  };

  const toggleEnabled = async (cfg, value) => {
    setBusy(cfg.action_type);
    try {
      await axios.put(`${API}/whatsapp-notifications/admin/${cfg.action_type}`, { is_enabled: value });
      setConfigs((prev) => prev.map((c) => c.action_type === cfg.action_type ? { ...c, is_enabled: value } : c));
    } catch (e) {
      toast.error("Failed to update");
    } finally { setBusy(""); }
  };

  const savePhones = async (cfg) => {
    if (!editPhones.length) { toast.error("Add at least one phone number"); return; }
    setBusy(cfg.action_type);
    try {
      await axios.put(`${API}/whatsapp-notifications/admin/${cfg.action_type}`, { recipient_phones: editPhones });
      toast.success("Recipients updated");
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusy(""); }
  };

  const remove = async (cfg) => {
    if (!window.confirm(`Delete the WhatsApp "${actionMeta(cfg.action_type).label}" notification?`)) return;
    setBusy(cfg.action_type);
    try {
      await axios.delete(`${API}/whatsapp-notifications/admin/${cfg.action_type}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error("Failed to delete");
    } finally { setBusy(""); }
  };

  const sendNow = async (cfg) => {
    setBusy(cfg.action_type);
    try {
      const res = await axios.post(`${API}/whatsapp-notifications/admin/trigger/${cfg.action_type}`);
      const r = res.data.result || {};
      if (r.sent > 0) {
        toast.success(`Sent to ${r.sent}/${r.total} numbers (${r.appointments ?? 0} appointments)`);
      } else if (r.skipped) {
        toast.info(r.reason || "Nothing to send");
      } else {
        toast.error(r.error || `Delivered to 0/${r.total ?? 0} numbers`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(""); }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-emerald-600" /> WhatsApp Notifications
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Send today's / tomorrow's appointment tables to admin numbers via WhatsApp (Gupshup).
            </p>
          </div>
          {canManage && (
            <Button onClick={openAdd} disabled={!available.length} style={{ backgroundColor: "#00A699" }}>
              <Plus className="w-4 h-4 mr-1" /> Add Notification
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : configs.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-500">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No WhatsApp notifications configured yet.
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {configs.map((cfg) => {
              const meta = actionMeta(cfg.action_type);
              const isEditing = editing === cfg.action_type;
              const b = busy === cfg.action_type;
              return (
                <Card key={cfg.action_type}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{meta.label}</p>
                          <Badge variant="outline" className={cfg.is_enabled ? "text-emerald-600 border-emerald-200" : "text-slate-400"}>
                            {cfg.is_enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                        {meta.schedule && (
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Auto-sends at {meta.schedule}
                          </p>
                        )}
                      </div>
                      {canManage && <Switch checked={!!cfg.is_enabled} disabled={b} onCheckedChange={(v) => toggleEnabled(cfg, v)} />}
                    </div>

                    <div>
                      <Label className="text-xs text-slate-500">Recipient numbers</Label>
                      {isEditing ? (
                        <div className="mt-1 space-y-2">
                          <PhoneChips phones={editPhones} onChange={setEditPhones} />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => savePhones(cfg)} disabled={b}>
                              {b ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {(cfg.recipient_phones || []).map((p) => (
                            <span key={p} className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">{p}</span>
                          ))}
                          {canManage && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600"
                              onClick={() => { setEditing(cfg.action_type); setEditPhones(cfg.recipient_phones || []); }}>
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {canManage && (
                      <div className="flex items-center gap-2 pt-1 border-t">
                        <Button size="sm" onClick={() => sendNow(cfg)} disabled={b} style={{ backgroundColor: "#00A699" }}>
                          {b ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send Now
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => remove(cfg)} disabled={b}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add WhatsApp Notification</DialogTitle>
            <DialogDescription>Configure a WhatsApp appointment digest.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Notification Type</Label>
              <Select value={form.action_type} onValueChange={(v) => setForm((f) => ({ ...f, action_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {available.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      <div>
                        <div className="font-medium">{a.label}</div>
                        <div className="text-xs text-slate-500">{a.schedule}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={form.is_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, is_enabled: v }))} />
            </div>
            <div className="space-y-1">
              <Label>Recipient Numbers *</Label>
              <PhoneChips phones={form.recipient_phones} onChange={(p) => setForm((f) => ({ ...f, recipient_phones: p }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy === "create"} style={{ backgroundColor: "#00A699" }}>
              {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default WhatsAppNotificationSetup;
